import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { 
    Mail, MessageSquare, Lightbulb, Briefcase, 
    ArrowRight, CheckCircle, Send, HelpCircle
} from 'lucide-react';
import { toast } from 'sonner';
import SEO, { SEO_CONFIG } from '../components/SEO';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_design-scorecard/artifacts/dmpk6oxa_site%20grade%20pro%20logo%20a.png";
const API = process.env.REACT_APP_BACKEND_URL;

export default function ContactPage({ onRunAudit, onNavigate }) {
    const seo = SEO_CONFIG.contact;
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        type: 'support',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const contactTypes = [
        { id: 'support', label: 'Support', icon: HelpCircle, description: 'Get help with your account or audits' },
        { id: 'feature', label: 'Feature Request', icon: Lightbulb, description: 'Suggest a new feature' },
        { id: 'business', label: 'Business Inquiry', icon: Briefcase, description: 'Partnerships or enterprise' }
    ];

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.name || !formData.email || !formData.message) {
            toast.error('Please fill in all fields');
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await fetch(`${API}/api/leads`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: formData.name,
                    email: formData.email,
                    message: `[${formData.type.toUpperCase()}] ${formData.message}`,
                    website: 'Contact Form Submission'
                })
            });

            if (response.ok) {
                setSubmitted(true);
                toast.success('Message sent successfully!');
            } else {
                throw new Error('Failed to send');
            }
        } catch (error) {
            toast.error('Failed to send message. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const faqs = [
        {
            question: "How accurate are the website audits?",
            answer: "Our AI analyzes over 50 data points to provide comprehensive insights. While no automated tool can replace a full manual review, our audits identify the most critical issues affecting your site's performance."
        },
        {
            question: "How often should I run an audit?",
            answer: "We recommend running an audit after any major website changes, or at least once a month to track improvements and catch new issues early."
        },
        {
            question: "Can I audit competitor websites?",
            answer: "Yes! You can analyze any publicly accessible website to see how your competitors are performing and identify opportunities."
        },
        {
            question: "What happens to my data?",
            answer: "We take privacy seriously. Your audit data is used solely to generate your report and is never shared with third parties. See our Privacy Policy for details."
        }
    ];

    return (
        <div className="min-h-screen bg-black text-white">
            <SEO title={seo.title} description={seo.description} keywords={seo.keywords} />
            
            <Navbar activePage="contact" onNavigate={onNavigate} onRunAudit={onRunAudit} />

            {/* Hero Section */}
            <section className="py-16 px-4">
                <div className="max-w-4xl mx-auto text-center">
                    <h1 className="text-4xl md:text-5xl font-bold mb-4">
                        Get in Touch
                    </h1>
                    <p className="text-xl text-gray-400">
                        Have a question or feedback? We'd love to hear from you.
                    </p>
                </div>
            </section>

            {/* Contact Form Section */}
            <section className="py-8 px-4">
                <div className="max-w-5xl mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                        {/* Form */}
                        <Card className="bg-white/5 border-white/10">
                            <CardContent className="p-8">
                                {submitted ? (
                                    <div className="text-center py-12">
                                        <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-6">
                                            <CheckCircle className="h-8 w-8 text-emerald-400" />
                                        </div>
                                        <h3 className="text-2xl font-bold text-white mb-2">Message Sent!</h3>
                                        <p className="text-gray-400 mb-6">We'll get back to you within 24-48 hours.</p>
                                        <Button 
                                            onClick={() => setSubmitted(false)}
                                            variant="outline"
                                            className="border-white/20 text-white hover:bg-white/10"
                                        >
                                            Send Another Message
                                        </Button>
                                    </div>
                                ) : (
                                    <form onSubmit={handleSubmit} className="space-y-6">
                                        <div>
                                            <label className="text-sm text-gray-400 mb-2 block">Inquiry Type</label>
                                            <div className="grid grid-cols-3 gap-3">
                                                {contactTypes.map((type) => {
                                                    const Icon = type.icon;
                                                    return (
                                                        <button
                                                            key={type.id}
                                                            type="button"
                                                            onClick={() => setFormData(prev => ({ ...prev, type: type.id }))}
                                                            className={`p-3 rounded-lg border transition-all text-center ${
                                                                formData.type === type.id 
                                                                    ? 'bg-magenta/20 border-magenta text-white' 
                                                                    : 'bg-black/30 border-white/10 text-gray-400 hover:border-white/30'
                                                            }`}
                                                        >
                                                            <Icon className="h-5 w-5 mx-auto mb-1" />
                                                            <span className="text-xs">{type.label}</span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        <div>
                                            <label className="text-sm text-gray-400 mb-2 block">Your Name</label>
                                            <Input
                                                type="text"
                                                placeholder="John Smith"
                                                value={formData.name}
                                                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                                className="bg-black/50 border-gray-800 text-white"
                                            />
                                        </div>

                                        <div>
                                            <label className="text-sm text-gray-400 mb-2 block">Email Address</label>
                                            <Input
                                                type="email"
                                                placeholder="john@example.com"
                                                value={formData.email}
                                                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                                                className="bg-black/50 border-gray-800 text-white"
                                            />
                                        </div>

                                        <div>
                                            <label className="text-sm text-gray-400 mb-2 block">Message</label>
                                            <textarea
                                                placeholder="How can we help you?"
                                                value={formData.message}
                                                onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                                                rows={5}
                                                className="w-full bg-black/50 border border-gray-800 rounded-md px-3 py-2 text-white placeholder:text-gray-600 focus:outline-none focus:border-magenta"
                                            />
                                        </div>

                                        <Button 
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="w-full bg-white text-black font-bold uppercase tracking-widest hover:bg-gray-200 rounded-sm py-6"
                                        >
                                            {isSubmitting ? 'Sending...' : 'Send Message'}
                                            <Send className="ml-2 h-4 w-4" />
                                        </Button>
                                    </form>
                                )}
                            </CardContent>
                        </Card>

                        {/* Contact Info */}
                        <div className="space-y-6">
                            <Card className="bg-white/5 border-white/10">
                                <CardContent className="p-6">
                                    <div className="flex items-start gap-4">
                                        <div className="w-12 h-12 rounded-lg bg-magenta/20 flex items-center justify-center shrink-0">
                                            <Mail className="h-6 w-6 text-magenta" />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-semibold text-white mb-1">Email Us</h3>
                                            <p className="text-gray-400 text-sm mb-2">For general inquiries and support</p>
                                            <a href="mailto:hello@sitegradepro.com" className="text-magenta hover:underline">
                                                hello@sitegradepro.com
                                            </a>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card className="bg-white/5 border-white/10">
                                <CardContent className="p-6">
                                    <div className="flex items-start gap-4">
                                        <div className="w-12 h-12 rounded-lg bg-[#FF4500]/20 flex items-center justify-center shrink-0">
                                            <svg className="h-6 w-6 text-[#FF4500]" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 01-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 01.042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 014.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 01.14-.197.35.35 0 01.238-.042l2.906.617a1.214 1.214 0 011.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 00-.231.094.33.33 0 000 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 00.029-.463.33.33 0 00-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 00-.232-.095z"/></svg>
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-semibold text-white mb-1">Join Our Reddit</h3>
                                            <p className="text-gray-400 text-sm mb-2">Discuss website optimization tips</p>
                                            <a 
                                                href="https://www.reddit.com/r/SiteGradePro/" 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className="text-[#FF4500] hover:underline"
                                                data-testid="contact-reddit-link"
                                            >
                                                r/SiteGradePro
                                            </a>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card className="bg-white/5 border-white/10">
                                <CardContent className="p-6">
                                    <div className="flex items-start gap-4">
                                        <div className="w-12 h-12 rounded-lg bg-[#5865F2]/20 flex items-center justify-center shrink-0">
                                            <svg className="h-6 w-6 text-[#5865F2]" viewBox="0 0 24 24" fill="currentColor"><path d="M20.317 4.37a19.791 19.791 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 00-5.487 0 12.64 12.64 0 00-.617-1.25.077.077 0 00-.079-.037A19.736 19.736 0 003.677 4.37a.07.07 0 00-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 00.031.057 19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 00-.041-.106 13.107 13.107 0 01-1.872-.892.077.077 0 01-.008-.128 10.2 10.2 0 00.372-.292.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 01.078.01c.12.098.246.198.373.292a.077.077 0 01-.006.127 12.299 12.299 0 01-1.873.892.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.839 19.839 0 006.002-3.03.077.077 0 00.032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/></svg>
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-semibold text-white mb-1">Join Our Discord</h3>
                                            <p className="text-gray-400 text-sm mb-2">Get help from the community</p>
                                            <a 
                                                href="https://discord.gg/GAfmKjNk" 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className="text-[#5865F2] hover:underline"
                                                data-testid="contact-discord-link"
                                            >
                                                discord.gg/GAfmKjNk
                                            </a>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card className="bg-gradient-to-br from-cyan-500/10 to-magenta/10 border-cyan-500/30">
                                <CardContent className="p-6 text-center">
                                    <h3 className="text-lg font-semibold text-white mb-2">Need Immediate Help?</h3>
                                    <p className="text-gray-400 text-sm mb-4">
                                        Run a free audit and see results instantly
                                    </p>
                                    <Button 
                                        onClick={onRunAudit}
                                        className="bg-white text-black font-bold uppercase tracking-widest hover:bg-gray-200 rounded-sm"
                                    >
                                        Run Free Audit
                                        <ArrowRight className="ml-2 h-4 w-4" />
                                    </Button>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="py-20 px-4 bg-gradient-to-b from-transparent to-white/5">
                <div className="max-w-4xl mx-auto">
                    <h2 className="text-3xl font-bold text-center mb-12">Frequently Asked Questions</h2>
                    <div className="space-y-4">
                        {faqs.map((faq, i) => (
                            <Card key={i} className="bg-white/5 border-white/10">
                                <CardContent className="p-6">
                                    <h3 className="text-lg font-semibold text-white mb-2">{faq.question}</h3>
                                    <p className="text-gray-400">{faq.answer}</p>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* Footer */}
            <Footer onNavigate={onNavigate} />
        </div>
    );
}
